/* AI-IMAGE-LAUNCH-03
   Owner: [data-ai-image-workspace] UI, input, status and result projection only.
   Execution authority: GCityExecutor -> Shopify app proxy -> server-side ledger/provider dispatch.
   Contract authority: server-rendered data attributes consumed by assets/gcity-api-integration.js.
   Must not call providers directly, derive a second execution contract or block on browser GFun state. */
(function () {
  'use strict';

  var VERSION = 'AI-IMAGE-LAUNCH-03';
  var ROOT_SELECTOR = '[data-ai-image-workspace]';
  var IS_ZH = String(document.documentElement.lang || '').toLowerCase().indexOf('zh') === 0;
  var COPY = IS_ZH ? {
    login_required: '請先登入後再生成。',
    unauthenticated_app_proxy: '登入狀態無法驗證，請重新登入後再試。',
    invalid_app_proxy_signature: '請求驗證失敗，請重新整理頁面後再試。',
    insufficient_gfun: 'GFun 餘額不足，請先補充。',
    provider_not_configured: '圖像服務尚未完成伺服器設定。',
    service_not_configured: '圖像服務尚未完成伺服器設定。',
    prompt_required: '請輸入圖像描述。',
    invalid_prompt: '提示詞長度或內容不符合要求。',
    consent_required: '請先確認內容與版權邊界。',
    generation_in_progress: '同一請求正在處理中，請稍候。',
    executor_missing: '生成元件尚未載入，請重新整理頁面。',
    contract_not_runnable: '目前產品生成服務尚未完成設定。',
    network_error: '網絡連線失敗，請稍後再試。',
    missing_image_result: '伺服器未返回可顯示的圖像結果。',
    failed: '生成失敗，請稍後再試。',
    working: '正在生成…',
    generate: '生成圖像',
    success: '生成完成。發布前請檢查事實、版權、人物與品牌邊界。',
    promptRangePrefix: '請使用 ',
    promptRangeSuffix: ' 個字元描述要生成的圖像。',
    validating: '正在安全驗證並生成圖像…',
    topup: '前往補充 GFun'
  } : {
    login_required: 'Sign in before generating.',
    unauthenticated_app_proxy: 'Your sign-in state could not be verified. Sign in again and retry.',
    invalid_app_proxy_signature: 'Request verification failed. Refresh the page and retry.',
    insufficient_gfun: 'Your GFun balance is insufficient. Add GFun before continuing.',
    provider_not_configured: 'The image service is not configured on the server yet.',
    service_not_configured: 'The image service is not configured on the server yet.',
    prompt_required: 'Enter an image description.',
    invalid_prompt: 'The prompt length or content is not valid.',
    consent_required: 'Confirm the content and rights boundary first.',
    generation_in_progress: 'The same request is already processing. Please wait.',
    executor_missing: 'The generation component is not loaded. Refresh the page.',
    contract_not_runnable: 'This product generation service is not configured yet.',
    network_error: 'Network connection failed. Please try again later.',
    missing_image_result: 'The server did not return a displayable image result.',
    failed: 'Generation failed. Please try again later.',
    working: 'Generating…',
    generate: 'Generate image',
    success: 'Generation complete. Review facts, copyright, people and brand boundaries before publishing.',
    promptRangePrefix: 'Use ',
    promptRangeSuffix: ' characters for the image description.',
    validating: 'Securely validating and generating the image…',
    topup: 'Add GFun'
  };

  function all(scope, selector) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(selector));
  }

  function clean(value) {
    return String(value == null ? '' : value).trim();
  }

  function bool(value) {
    return value === true || value === 'true' || value === '1';
  }

  function number(value, fallback) {
    var parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function hash(input) {
    var value = String(input || '');
    var output = 2166136261;
    for (var i = 0; i < value.length; i += 1) {
      output ^= value.charCodeAt(i);
      output = Math.imul(output, 16777619);
    }
    return Math.abs(output >>> 0).toString(36);
  }

  function idempotencyKey(root, prompt, size, quality) {
    var minute = Math.floor(Date.now() / 60000);
    return ['ai-image', root.dataset.productId || root.dataset.productHandle || 'product', hash([prompt, size, quality].join('|')), minute].join('_');
  }

  function errorCode(result) {
    var error = result && result.error;
    if (error && typeof error === 'object') return clean(error.code || error.reason);
    return clean(result && (result.reason || result.code || error));
  }

  function errorMessage(result) {
    var error = result && result.error;
    if (error && typeof error === 'object') return clean(error.message);
    return clean(result && result.message);
  }

  function messageFor(result) {
    var reason = errorCode(result);
    return COPY[reason] || errorMessage(result) || COPY.failed;
  }

  function safeImageUrl(value) {
    var raw = clean(value);
    if (!raw) return '';
    if (raw.indexOf('data:image/') === 0 || raw.indexOf('blob:') === 0) return raw;

    try {
      var parsed = new URL(raw, window.location.origin);
      if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return '';
      return parsed.href;
    } catch (_error) {
      return '';
    }
  }

  function costFor(root) {
    return number(root.dataset.gfunConsumePerCall || root.dataset.gfunCost, 0);
  }

  function mount(root) {
    if (!root || root.dataset.aiImageMounted === 'true') return;
    root.dataset.aiImageMounted = 'true';
    root.setAttribute('data-ai-image-runtime-version', VERSION);
    root.setAttribute('data-ai-image-runtime-owner', 'assets/ai-image-workspace.js');
    root.setAttribute('data-ai-image-execution-owner', 'assets/gcity-api-integration.js');
    root.setAttribute('data-ai-image-selector-contract', ROOT_SELECTOR);

    var form = root.querySelector('[data-ai-image-form]');
    if (!form) return;

    var prompt = root.querySelector('[data-ai-image-prompt]');
    var size = root.querySelector('[data-ai-image-size]');
    var quality = root.querySelector('[data-ai-image-quality]');
    var consent = root.querySelector('[data-ai-image-consent]');
    var submit = root.querySelector('[data-ai-image-submit]');
    var status = root.querySelector('[data-ai-image-status]');
    var result = root.querySelector('[data-ai-image-result]');
    var placeholder = root.querySelector('[data-ai-image-placeholder]');
    var figure = root.querySelector('[data-ai-image-figure]');
    var image = root.querySelector('[data-ai-image-output]');
    var resultActions = root.querySelector('[data-ai-image-result-actions]');
    var download = root.querySelector('[data-ai-image-download]');
    var history = root.querySelector('[data-ai-image-history]');
    var reset = root.querySelector('[data-ai-image-reset]');
    var balance = root.querySelector('[data-ai-image-balance]');
    var idleSubmitLabel = submit ? clean(submit.textContent) : '';

    if (balance) balance.setAttribute('data-gfun-display-authority', 'server-response-only');

    function setBusy(active) {
      if (submit) {
        submit.disabled = !!active;
        submit.setAttribute('aria-busy', active ? 'true' : 'false');
        submit.textContent = active
          ? (root.dataset.workingLabel || COPY.working)
          : (idleSubmitLabel || COPY.generate);
      }
      if (result) result.setAttribute('aria-busy', active ? 'true' : 'false');
    }

    function setStatus(text, state) {
      if (!status) return;
      status.hidden = !text;
      status.textContent = text || '';
      status.dataset.state = state || 'idle';
    }

    function showResult(data) {
      data = data || {};
      var imageUrl = safeImageUrl(data.image_url || data.imageUrl || data.url || data.data_url);
      var resultId = clean(data.result_id || data.resultId || data.task_id || data.taskId);
      if (!imageUrl) throw new Error('missing_image_result');
      if (placeholder) placeholder.hidden = true;
      if (figure) figure.hidden = false;
      if (image) {
        image.src = imageUrl;
        image.width = size && size.value === '1536x1024' ? 1536 : 1024;
        image.height = size && size.value === '1024x1536' ? 1536 : 1024;
      }
      if (resultActions) resultActions.hidden = false;
      if (download) download.href = imageUrl;
      if (history && resultId) {
        var historyUrl = new URL(root.dataset.historyUrl || '/pages/result-history', window.location.origin);
        historyUrl.searchParams.set('result_id', resultId);
        historyUrl.searchParams.set('source', 'ai-image-generator');
        history.href = historyUrl.pathname + historyUrl.search + historyUrl.hash;
      }
      if (typeof data.gfun_balance === 'number' && balance) balance.textContent = String(data.gfun_balance);
      setStatus(COPY.success, 'success');
      try {
        document.dispatchEvent(new CustomEvent('ai-image:result:ready', { detail: { result_id: resultId, image_url: imageUrl } }));
      } catch (_error) {}
    }

    function resetResult() {
      if (image) image.removeAttribute('src');
      if (download) download.removeAttribute('href');
      if (figure) figure.hidden = true;
      if (resultActions) resultActions.hidden = true;
      if (placeholder) placeholder.hidden = false;
      setStatus('', 'idle');
      if (prompt) prompt.focus();
    }

    all(root, '[data-ai-image-example]').forEach(function (button) {
      button.addEventListener('click', function () {
        if (!prompt) return;
        prompt.value = button.getAttribute('data-ai-image-example') || '';
        prompt.focus();
      });
    });

    if (reset) reset.addEventListener('click', resetResult);

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      var promptValue = clean(prompt && prompt.value);
      var minLength = number(prompt && prompt.getAttribute('minlength'), 8);
      var maxLength = number(prompt && prompt.getAttribute('maxlength'), 1800);

      if (!bool(root.dataset.loggedIn)) {
        window.location.href = root.dataset.loginUrl || '/account';
        return;
      }
      if (promptValue.length < minLength || promptValue.length > maxLength) {
        setStatus(COPY.promptRangePrefix + minLength + '–' + maxLength + COPY.promptRangeSuffix, 'error');
        if (prompt) prompt.focus();
        return;
      }
      if (!consent || !consent.checked) {
        setStatus(COPY.consent_required, 'error');
        return;
      }
      if (!window.GCityExecutor || typeof window.GCityExecutor.execute !== 'function') {
        setStatus(messageFor({ reason: 'executor_missing' }), 'error');
        return;
      }

      var cost = costFor(root);

      resetResult();
      setBusy(true);
      setStatus(COPY.validating, 'working');

      var sizeValue = size ? size.value : '1024x1024';
      var qualityValue = quality ? quality.value : 'medium';
      var idem = idempotencyKey(root, promptValue, sizeValue, qualityValue);
      var context = {
        api_family: root.dataset.apiFamily || '',
        canonical_endpoint: root.dataset.canonicalEndpoint || '',
        delivery_type: root.dataset.deliveryType || '',
        delivery_method: root.dataset.deliveryMethod || '',
        engine_type: root.dataset.engineType || '',
        result_surface: root.dataset.resultSurface || '',
        ui_mode: root.dataset.uiMode || '',
        service_active: root.dataset.serviceActive !== 'false',
        service_cta_type: root.dataset.serviceCtaType || '',
        requires_gfun: bool(root.dataset.requiresGfun),
        requires_entitlement: bool(root.dataset.requiresEntitlement),
        gfun_consume_per_call: cost,
        gfun_reward_on_completion: number(root.dataset.gfunRewardOnCompletion, 0),
        product_handle: root.dataset.productHandle || 'ai-image-generator',
        product_id: root.dataset.productId || '',
        idempotency_key: idem,
        data: {
          prompt: promptValue,
          size: sizeValue,
          quality: qualityValue,
          n: 1,
          consent: true,
          idempotency_key: idem,
          source: 'product.ai-image-generator'
        }
      };

      window.GCityExecutor.execute(context, { skipGate: false })
        .then(function (response) {
          if (!response || response.ok !== true || response.success === false) {
            var failure = response || { reason: 'generation_failed' };
            if (errorCode(failure) === 'insufficient_gfun' && root.dataset.gfunUrl) {
              var link = document.createElement('a');
              link.href = root.dataset.gfunUrl;
              link.textContent = COPY.topup;
              link.className = 'ai-image-workspace__status-link';
              setStatus(messageFor(failure) + ' ', 'error');
              if (status) status.appendChild(link);
            } else {
              setStatus(messageFor(failure), 'error');
            }
            return;
          }
          showResult(response.data || response);
        })
        .catch(function (error) {
          setStatus(messageFor({ reason: clean(error && error.message) || 'network_error' }), 'error');
        })
        .finally(function () { setBusy(false); });
    });
  }

  function boot(scope) {
    var root = scope || document;
    if (root.matches && root.matches(ROOT_SELECTOR)) mount(root);
    all(root, ROOT_SELECTOR).forEach(mount);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(document); }, { once: true });
  else boot(document);
  document.addEventListener('shopify:section:load', function (event) { boot(event.target || document); });
})();
